import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  NumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import api from '../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPatch,
  useApiPost,
  useApiPut
} from '../common/api/Api.hooks';
import { useIdSearchableFetch } from '../common/entityId.hooks';
import { PaginatedList } from '../common/PaginatedList.interface';
import { useRecaptchaEnterprise } from '../hooks/recaptcha/useRecaptchaEnterprise';
import { LoginContext } from '../login/Login.context';
import { Signup } from '../login/Signup.interface';
import { DashboardChartData } from '../secure-portal/dashboard/Dashboard.interface';
import { FileDto } from '../secure-portal/documents/Document.interface';
import { Task, TaskSummaryItem } from '../secure-portal/tasks/Task.interface';
import { TrainingUser } from '../secure-portal/training/Training.interface';
import {
  isValidUserStatus,
  UserListFilterOptions
} from '../secure-portal/users/components/UserListFilter';
import { JwtToken } from './JwtToken.interface';
import {
  clearAccessTokens,
  UserContext,
  UserContextType
} from './User.context';
import {
  TrainingStats,
  User,
  UserRegisterDto,
  UserStats,
  UserSummary,
  UserTrainingUpcoming
} from './User.interface';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface PasswordRecoverRequest {
  email: string;
}

export function useGetCurrentUser() {
  const { data, isLoading, error, get } =
    useApiGet<UserContextType>('users/me');
  let user = data;
  if (!api.getAccessToken() || error) {
    // if no access token set cached user to null
    user = null; // user == null (not logged in)
  }
  return { user, isLoading, error, get };
}

export function useLogin() {
  const [jwtToken, setJwtToken] = useState<JwtToken>();
  const { user, setUser } = useContext(UserContext);
  const { setDidLogout } = useContext(LoginContext);
  const { data, isLoading, error, post } = useApiPost<JwtToken>('users/login');

  useEffect(() => {
    if (data) {
      const jwt = data;
      setJwtToken(jwt);
      api.setAccessToken(jwt.accessToken);
      setUser(jwt.user);
      setDidLogout(false);
    }
  }, [data, setUser, setDidLogout]);
  const {
    loadingRecaptchaToken,
    loadingRecaptchaTokenError,
    getRecaptchaToken
  } = useRecaptchaEnterprise();
  const doLogin = useCallback(
    async (email: string, password: string) => {
      const token = await getRecaptchaToken('login');
      post({ email, password }, { recaptcha: token });
    },
    [post]
  );
  return {
    jwtToken,
    user,
    isLoading: isLoading || loadingRecaptchaToken,
    error: error || loadingRecaptchaTokenError,
    doLogin
  };
}

export function useJiraLogin() {
  const { user, setUser } = useContext(UserContext);
  const { setDidLogout } = useContext(LoginContext);
  const { data, isLoading, error, post } =
    useApiPost<JwtToken>('jira-connect/login');

  useEffect(() => {
    if (data && Array.isArray(data)) {
      const jwt = data[0],
        jiraJwt = data[1];
      api.setAccessToken(jwt['accessToken']);
      api.setAccessToken(
        jiraJwt['access_token'],
        'atlassian',
        jiraJwt['expires_in']
      );
      setUser(jwt.user);
      setDidLogout(false);
    }
  }, [data, setUser, setDidLogout]);

  const jiraLogin = useCallback(
    (token: string, email?: string) => {
      post({ token, email });
    },
    [post]
  );

  return { data, user, isLoading, error, jiraLogin };
}

export function useLogout() {
  const { setUser } = useContext(UserContext);
  const { setDidLogout } = useContext(LoginContext);
  const { isLoading, error, post } = useApiPost<User>('users/logout');

  // useEffect(() => {
  //   if (response && response.ok) {
  //     console.log('user logged out', response.ok);
  //     api.removeAccessToken();
  //     setUser(null);
  //   }
  // }, [response, setUser]);

  const doLogout = useCallback(() => {
    post();
    // NOTE: logout endpoint doesn't return anything so above
    // useEffect() won't work
    clearAccessTokens();
    setUser(null);
    setDidLogout(true);
    window.location.reload();
  }, [post, setUser, setDidLogout]);

  return { isLoading, error, doLogout };
}

export function useFindUsers(shouldFetchOnMount = false) {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<User>>('users');
  const findUsers = useIdSearchableFetch(get, {
    from: 'filter',
    entity: 'User'
  });
  useEffect(() => {
    if (shouldFetchOnMount) {
      findUsers();
    }
  }, [findUsers, shouldFetchOnMount]);
  return { userList: data, isLoading, error, get: findUsers };
}

export function useFindTrainees() {
  const { data, get, ...result } =
    useApiGet<PaginatedList<User>>(`users/trainees`);
  const findTrainees = useIdSearchableFetch(get, 'User');
  return { ...result, users: data, get: findTrainees };
}

export function useCreateUser() {
  const { data, isLoading, error, post } = useApiPost<User>('users/create');
  return { user: data, isLoading, error, post };
}

export function useRegisterUser() {
  const { data, isLoading, error, post } = useApiPost<User>('users/register');
  const {
    loadingRecaptchaToken,
    loadingRecaptchaTokenError,
    getRecaptchaToken
  } = useRecaptchaEnterprise();
  const register = useCallback(
    async (dto: UserRegisterDto) => {
      const token = await getRecaptchaToken('register');
      post(dto, { recaptcha: token });
    },
    [getRecaptchaToken, post]
  );
  return {
    user: data,
    isLoading: isLoading || loadingRecaptchaToken,
    error: error || loadingRecaptchaTokenError,
    register
  };
}

export function useSendInvite(id: number) {
  const { data, isLoading, error, post } = useApiPost<User>(
    `users/${id}/send-invitaion`
  );
  return { data, isLoading, error, post };
}

export function useNotifySupport(id: number) {
  const { data, isLoading, error, post } = useApiPost<Signup>(
    `users/${id}/notify-support`
  );
  return { data, isLoading, error, notifySupport: post };
}

export function useVerifyEmail() {
  const { data, isLoading, error, get } =
    useApiGet<JwtToken>('users/verify/email');
  return { emailVerified: data, isLoading, error, get };
}

export function useFindUser(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<User>(`users/find/${id}`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, id, shouldFetchOnMount]);
  return { user: data, isLoading, error, get };
}

export function usePasswordRecover() {
  const {
    data: success,
    isLoading,
    error,
    post
  } = useApiPost<boolean>('users/password/recover');
  const {
    loadingRecaptchaToken,
    loadingRecaptchaTokenError,
    getRecaptchaToken
  } = useRecaptchaEnterprise();
  const doPasswordRecover = useCallback(
    async (email: string) => {
      const token = await getRecaptchaToken('recover');
      post({ email }, { recaptcha: token });
    },
    [post]
  );
  return {
    success,
    isLoading: isLoading || loadingRecaptchaToken,
    error: error || loadingRecaptchaTokenError,
    doPasswordRecover
  };
}

export function usePasswordVerifyResetToken() {
  const {
    data: user,
    isLoading,
    error,
    get
  } = useApiGet<boolean>('users/password/verify/reset-token');

  const doVerifyResetToken = useCallback(
    (token: string) => {
      get({ token });
    },
    [get]
  );

  return { user, isLoading, error, doVerifyResetToken };
}

export function usePasswordReset() {
  const { user, doVerifyResetToken, ...verify } = usePasswordVerifyResetToken();

  let {
    data: success,
    isLoading,
    error,
    put
  } = useApiPut<boolean>('users/password/reset');

  isLoading = isLoading || verify.isLoading;

  const doPasswordReset = useCallback(
    (password: string, token: string) => {
      put({ password, token });
    },
    [put]
  );

  return {
    user,
    success,
    isLoading,
    error,
    verifyError: verify.error,
    doPasswordReset,
    doVerifyResetToken
  };
}

export function usePatchUser(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<User>(`users/${id}`);
  return { user: data, isLoading, error, patch };
}

export function useGetUserTrainings(
  shouldFetchOnMount = false,
  userId: number | undefined
) {
  const { data, isLoading, error, get } = useApiGet<TrainingUser[]>(
    `users/${userId}/trainings`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { userTrainings: data, isLoading, error, get };
}

export function useGetUserPendingTrainings(userId: number) {
  return useApiGet<PaginatedList<TrainingUser>>(
    `users/${userId}/trainings/pending`
  );
}

export function useGetUserPendingTrainingStats(
  userId: number,
  shouldFetchOnMount: boolean = false
) {
  const result = useApiGet<DashboardChartData>(
    `users/${userId}/trainings/pending/stats`
  );

  useEffect(() => {
    shouldFetchOnMount && result.get();
  }, [shouldFetchOnMount, result.get]);

  return result;
}

export function useGetUserPendingDocuments(userId: number) {
  return useApiGet<PaginatedList<FileDto>>(`files/user/${userId}`);
}

export function useGetUserPendingDocumentStats(
  userId: number,
  shouldFetchOnMount: boolean = false
) {
  const result = useApiGet<DashboardChartData>(`files/user/${userId}/stats`);

  useEffect(() => {
    shouldFetchOnMount && result.get();
  }, [shouldFetchOnMount, result.get]);

  return result;
}

export function useGetUserTrainingStats(
  shouldFetchOnMount = false,
  userId: number | undefined
) {
  const { data, isLoading, error, get } = useApiGet<TrainingStats>(
    `users/${userId}/training-stats`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);

  return { data, isLoading, error, get };
}

export function useUserTrainingUpcomingCount(
  shouldFetchOnMount = false,
  userId: number | undefined
) {
  const { data, isLoading, error, get } = useApiGet<number>(
    `users/${userId}/training-upcoming/count`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);

  return { data, isLoading, error, get };
}

export function useUserTrainingUpcoming(
  shouldFetchOnMount = false,
  userId: number | undefined
) {
  const { data, isLoading, error, get } = useApiGet<UserTrainingUpcoming[]>(
    `users/${userId}/training-upcoming`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);

  return { data, isLoading, error, get };
}

export function useGetCurrentUserTasks() {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<Task>>('users/me/tasks');
  return { data, isLoading, error, get };
}

export function useGetUserTaskStats() {
  return useApiGet<DashboardChartData>('users/me/tasks/stats');
}

export function useGetUserTaskSummary() {
  return useApiGet<TaskSummaryItem[]>('users/me/tasks/summary');
}

export function useGetEmployeeStats(): [
  ApiResult<UserStats>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<UserStats>('users/stats');
  return [result, get];
}

export function useBlockDeactivatedUser(state: ApiResult<any>) {
  const { setUser } = useContext(UserContext);
  const { setDidLogout } = useContext(LoginContext);

  useEffect(() => {
    const { error } = state;
    if (!error) {
      return;
    }
    if (error.statusCode === 403 && error.error === 'Deactivated') {
      // logout
      clearAccessTokens();
      setUser(null);
      setDidLogout(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}

export function useUserFilterQueryParams() {
  const [groupIds, setGroupIds] = useQueryParam(
    'groupIds',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [status, setStatus] = useQueryParam(
    'status',
    withDefault(StringParam, undefined)
  );

  // only set properties that have values
  const filterOptions = useCallback((options: UserListFilterOptions) => {
    const { groupIds, status } = options;
    const opts: Partial<UserListFilterOptions> = {};

    if (groupIds && groupIds.length > 0) {
      opts.groupIds = groupIds;
    }
    if (status) {
      opts.status = status;
    }

    // return only if at least one property is available
    return Object.keys(opts).length > 0 ? opts : undefined;
  }, []);

  const options = useMemo(() => {
    return filterOptions({
      groupIds,
      status: isValidUserStatus(status) ? status : undefined
    });
  }, [groupIds, status, filterOptions]);

  const update = useCallback(
    (options: Partial<UserListFilterOptions>) => {
      setGroupIds(options.groupIds || []);
      setStatus(options.status);
    },
    [setGroupIds, setStatus]
  );

  return { options, update, filterOptions };
}

export function useGetUserSummary() {
  const { data, get, ...result } = useApiGet<UserSummary>('users/summary');

  return { ...result, userSummary: data, getUserSummary: get };
}
